import { FormikProvider, useFormik } from "formik";
import { useEffect, useState, Fragment, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
	CardBody,
	CardContainer,
	CardHeader,
} from "../../../components/DashboardCard/DashboardCard";
import Loading from "../../../components/Loading/Loading";
import * as Yup from "yup";
import styled, { useTheme } from "styled-components";
import StyledForm from "../../../components/Form/Form";
import TextField from "../../../components/FormFields/TextField";
import Button from "../../../components/Button/Button";
import DropdownField from "../../../components/FormFields/DropdownField";
import { CountryOptions } from "../../../data/CountryData";
import {
	StickyCardBody,
	StickyContainer,
} from "../../../components/StickyCard/StickyCard";
import { EntryReviewContainer, EntryField } from "../../Entries/EntryForm";

import { NemoUser } from "../../../hooks/useCurrentUser";
import { JobTitleOptions } from "../../../data/JobTitleData";
import CheckboxField from "../../../components/FormFields/CheckboxField";

import axios from "axios";
import {
	urlCompanies,
	urlCompanyRelationships,
	urlEntries,
	urlManageCompanies,
} from "../../../endpoints";
import { CompanyTypeOptions } from "../Program/ProgramInterfaces";
import Table from "../../../components/Table/Table";
import Placeholder from "../../../assets/placeholders/placeholder.png";
import AdjustmentFields from "./AdjustmentFields";
import EntryGrid from "../../../components/EntryGrid/EntryGrid";
import { CollapsibleContainer } from "../../../components/Collapsible/Collapsible";
import BrowserHeader from "../../../components/BrowserHeader/BrowserHeader";
import { useAlert } from "../../../components/Alert/Alerts";
import { getLocalTime } from "../../../utils/timeUtilities";
import assetsConfig from "../../../assetsConfig";
import { NewUserModal } from "../../../components/NewUserModal/NewUserModal";
import Authorized from "../../../components/Auth/Authorized";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { adminEnable } from "../User/managerUser";
import DynamicDropdownField from "../../../components/FormFields/DynamicDropdownField";
import * as XLSX from "xlsx";
import {
	PaymentStatus,
	ReviewStatusOptions,
} from "../../Checkout/OrderInterfaces";
import { getHierarchy } from "../../Checkout/manageCheckout";
import { EntrySearchResult } from "../Search/EntrySearchTab";
import config from "../../../config";
import ToggleSwitchField from "../../../components/FormFields/ToggleSwitchField";
import { DropdownOptions } from "../../../components/FormFields/DropdownField";
import SearchBar from "../../../components/SearchBar/SearchBar";
import { CompanyModel } from "../../Company/company.model";
import { NewCompanyModal } from "../../../components/NewCompanyModal/NewCompanyModal";
import { CanadaStateOptions, USStateOptions } from "../../../data/StateData";

const StyledLink = styled.a`
	color: ${({ theme }) => theme.colorActivation};
`;

const LoadingSpinner = styled.img`
	width: 18px;
	display: inline-flex;
`;

const CompanyInfo = styled.div`
	padding: 2.25rem;
	/* border: solid 2px ${({ theme }) => theme.colorBorderLight}; */
	background: ${({ theme }) => theme.colorBackgroundLightLight};
`;

export enum CompanyRelationshipType {
	Canonical,
	BillingOwner,
	JurorAffiliation,
}

interface Variation {
	id: number;
	name: string;
	city: string;
	country: string;
}

const EditCompany = () => {
	const { addNewAlert } = useAlert();
	const theme = useTheme();
	const { companyId } = useParams<{ companyId: string }>();
	const [companyUsers, setCompanyUsers] = useState<NemoUser[]>([]);
	const [company, setCompany] = useState<CompanyModel>({
		id: 0,
		address1: "",
		address2: "",
		city: "",
		companyType: "",
		entryContactEmail: "",
		entryContactJobTitle: "",
		entryContactName: "",
		entryContactPhone: "",
		financeContactEmail: "",
		financeContactJobTitle: "",
		financeContactName: "",
		financeContactPhone: "",
		name: "",
		postalCode: "",
		socialMedia1: "",
		socialMedia2: "",
		socialMedia3: "",
		socialMedia4: "",
		state: "",
		country: "",
		invoiceContactName: "",
		invoiceEmail: "",
		invoicePhone: "",
		logo: "",
		blockDelayedPayments: false,
		overridePaymentMethod: false,
		createdDate: "",
		createdBy: "",
		updatedDate: "",
		modifiedBy: "",
		adjustments: [],
		agencyNetworkId: undefined,
		holdingCompanyId: undefined,
		isCanonical: false,
		canonicalCompanyId: 0,
	});
	const [isCompanyCollapsed, setisCompanyCollapsed] = useState(false);
	const [isMetadataCollapsed, setIsMetadataCollapsed] = useState(true);
	const [isUserdataCollapsed, setIsUserdataCollapsed] = useState(true);
	const [isEntryDataCollapsed, setIsEntryDataCollapsed] = useState(true);
	const [isPaymentSettingsCollapsed, setIsPaymentSettingsCollapsed] =
		useState(true);
	const [companyEntries, setCompanyEntries] = useState<EntrySearchResult[]>([]);

	const [showModal, setShowModal] = useState(false);
	const [showCompanyModal, setShowCompanyModal] = useState(false);
	const [isExporting, setIsExporting] = useState(false);

	const exportAllCompanyEntriesToExcel = async () => {
		setIsExporting(true);
		try {
			const allEntries = await fetchAllCompanyEntries(Number(companyId));

			if (!allEntries || allEntries.length === 0) return;

			const data = allEntries.map((entry) => {
				const hierarchy = getHierarchy(
					entry.executions,
					entry.ancestry,
					entry.isCampaign
				);

				return {
					"Company ID": entry.companyId,
					"Company Name": entry.companyName,
					"Company Type": entry.companyType || "",
					"Agency Network": entry.agencyNetwork || "",
					"Holding Company": entry.holdingCompany || "",
					"Entry ID": entry.id,
					Title: entry.title,
					Brand: entry.brand,
					"Level 1": hierarchy[0] || "",
					"Level 2": hierarchy[1] || "",
					"Level 3": hierarchy[2] || "",
					"Level 4": hierarchy[3] || "",
					"Level 5": hierarchy[4] || "",
					"Payment Status": PaymentStatus[entry.paymentStatusId] || "",
					"Review Status":
						ReviewStatusOptions[entry.reviewStatusId]?.label || "",
					"Execution Count": entry.executions?.length || 0,
					"Award Level": entry.awardLevel || "",
				};
			});

			const worksheet = XLSX.utils.json_to_sheet(data);
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, "Entries");

			const fileName = `${allEntries[0].companyName}_Entries_Export.xlsx`;
			XLSX.writeFile(workbook, fileName);
		} finally {
			setIsExporting(false);
		}
	};

	const fetchAllCompanyEntries = async (
		companyId: number
	): Promise<EntrySearchResult[]> => {
		const allEntries: EntrySearchResult[] = [];
		const pageSize = 100; // Adjust if needed
		let page = 1;
		let totalPages = 1;

		while (page <= totalPages) {
			const response = await axios.post(
				`${urlEntries}/search`,
				{ companyId },
				{ params: { page, recordsPerPage: pageSize } }
			);

			if (response.status === 200) {
				const newEntries = response.data;
				const totalRecords = parseInt(response.headers["totalamountofrecords"]);
				totalPages = Math.ceil(totalRecords / pageSize);

				allEntries.push(...newEntries);
			}

			page++;
		}

		return allEntries;
	};

	async function UpdateCompany(updatedCompany: CompanyModel) {
		// console.log(updatedCompany)

		axios
			.put(`${urlManageCompanies}/${updatedCompany.id}`, {
				...updatedCompany,
			})
			.then((res) => {
				if (res.status === 204) {
					addNewAlert({ type: "success", message: "Successfully Updated" });
					setCompany(updatedCompany);
				}
			})
			.catch((err) => {
				addNewAlert({
					type: "error",
					message: "Server error. Please try again later",
				});
			});
	}

	async function getCompany(companyId: number) {
		const response = axios.get<CompanyModel>(
			`${urlManageCompanies}/getCompany/${companyId}`
		);
		return response;
	}

	async function getCompanyUsers(companyId: number) {
		try {
			const response = await axios.get<NemoUser[]>(
				`${urlManageCompanies}/getCompanyUsers/${companyId}`
			);
			if (response.status === 200) {
				return response.data;
			}
		} catch (error) {
			addNewAlert({
				type: "error",
				message: "Server error. Please try again later",
			});
		}
	}

	async function getCanonicalCompanies() {
		try {
			const response = await axios.get<DropdownOptions[]>(
				`${urlCompanies}/canonical`
			);
			if (response.status === 200) {
				return response.data;
			}
		} catch (error) {
			addNewAlert({
				type: "error",
				message: "Server error. Please try again later",
			});
		}
	}

	async function createCanonicalRelationship(
		thisCompanyId: number,
		canonicalCompanyId: number
	) {
		return await axios.post(`${urlCompanyRelationships}`, {
			SubjectCompanyId: thisCompanyId,
			ObjectCompanyId: canonicalCompanyId,
			RelationshipType: CompanyRelationshipType.Canonical,
		});
	}

	function deleteCanonicalRelationship(subjectId: number) {
		return axios.delete(`${urlCompanyRelationships}`, {
			params: {
				subjectId,
				relationshipType: CompanyRelationshipType.Canonical,
			},
		});
	}

	function setCanonicalRelationship(subjectId: number, canonicalId: number) {
		return axios.post(`${urlCompanyRelationships}`, {
			subjectCompanyId: subjectId,
			objectCompanyId: canonicalId,
			relationshipType: CompanyRelationshipType.Canonical,
		});
	}

	// ────────────────────────────────────────────────────────────────
	// Helper (one call handles insert / update / delete)
	// ────────────────────────────────────────────────────────────────
	function upsertCanonicalRelationship(
		variationId: number,
		canonicalId: number | null // null → unlink
	) {
		return axios.put(`${urlCompanyRelationships}/${variationId}/canonical`, {
			canonicalCompanyId: canonicalId, // matches CanonicalLinkDto
		});
	}

	const fetchVariations = async (canonicalCompanyId: number) => {
		const res = await axios.get<Variation[]>(
			`${urlCompanyRelationships}/subjects`,
			{
				params: {
					relationshipType: CompanyRelationshipType.Canonical,
					objectId: canonicalCompanyId,
				},
			}
		);
		return res.data;
	};

	const fetchCanonicalCompany = async (variationCompanyId: number) => {
		const res = await axios.get<Variation[]>(
			`${urlCompanyRelationships}/objects`,
			{
				params: {
					relationshipType: CompanyRelationshipType.Canonical,
					subjectId: variationCompanyId,
				},
			}
		);
		return res.data;
	};

	const [variations, setVariations] = useState<Variation[]>([]);
	const [canonicalCompanies, setCanonicalCompanies] = useState<
		DropdownOptions[]
	>([]);
	const [canonicalQuery, setCanonicalQuery] = useState("");

	function debounce<F extends (...args: any[]) => void>(fn: F, delay = 250) {
		let timer: ReturnType<typeof setTimeout> | null = null;
		return (...args: Parameters<F>) => {
			if (timer) clearTimeout(timer);
			timer = setTimeout(() => fn(...args), delay);
		};
	}

	const doSearch = useMemo(
		() =>
			debounce(async (text: string) => {
				if (!text.trim()) return setCanonicalCompanies([]);
				const res = await axios.get<DropdownOptions[]>(
					`${urlCompanies}/searchCanonical`,
					{
						params: { query: text, limit: 15 },
					}
				);
				setCanonicalCompanies(res.data);
			}, 250),
		[]
	);

	useEffect(() => {
		getCompany(Number(companyId))
			.then((response) => {
				if (response.status === 200) {
					setCompany(response.data);
				}
			})
			.catch((error) => {
				// console.log("getOrder ERROR", error);
				// setErrors((old) => [...old, error]);
			});

		getCompanyUsers(Number(companyId)).then((response) => {
			setCompanyUsers(response!);
		});

		// temporary solution
		getCanonicalCompanies().then((response) => {
			setCanonicalCompanies(response || []);
		});
	}, []);

	const fieldRequired = "This field is required";
	const formikProps = useFormik({
		initialValues: company,
		enableReinitialize: true,
		onSubmit: async (value) => {},
		validationSchema: Yup.object({
			name: Yup.string().required(fieldRequired),
			companyType: Yup.string().nullable().required(fieldRequired),
			city: Yup.string()
				.nullable()
				.when([], {
					is: () => settings.featureFlags["Company Address"],
					then: (schema) => schema.required(fieldRequired),
					otherwise: (schema) => schema.notRequired(),
				}),
			country: Yup.string()
				.nullable()
				.when([], {
					is: () => settings.featureFlags["Company Address"],
					then: (schema) => schema.required(fieldRequired),
					otherwise: (schema) => schema.notRequired(),
				}),
			state: Yup.string()
				.nullable()
				.when("country", {
					is: (value: string) =>
						value === "United States" ||
						(value === "Canada" && settings.featureFlags["Company Address"]),
					then: Yup.string().required(fieldRequired),
				}),
			address1: Yup.string()
				.nullable()
				.when([], {
					is: () => settings.featureFlags["Company Address"],
					then: (schema) => schema.required(fieldRequired),
					otherwise: (schema) => schema.notRequired(),
				}),
			postalCode: Yup.string()
				.nullable()
				.when("country", {
					is: (value: string) =>
						value === "United States" ||
						(value === "Canada" && settings.featureFlags["Company Address"]),
					then: Yup.string().required(fieldRequired),
				}),
			agencyNetworkId: Yup.number()
				.nullable()
				.min(1, fieldRequired)
				.when([], {
					is: () => settings.featureFlags["Company Agency Network"],
					then: (schema) => schema.required(fieldRequired),
					otherwise: (schema) => schema.notRequired(),
				}),
			holdingCompanyId: Yup.number()
				.nullable()
				.min(1, fieldRequired)
				.when([], {
					is: () => settings.featureFlags[`Company Holding Company`],
					then: (schema) => schema.required(fieldRequired),
					otherwise: (schema) => schema.notRequired(),
				}),
			// phone: Yup.string().required(fieldRequired),
			// adjustments: Yup.array().of(
			//   Yup.object({
			//     programId: Yup.string().required("Program is required"),
			//     adjustmentDate: Yup.date().required("Adjustment date is required"),
			//     expirationDate: Yup.date()
			//       .required("Expiration date is required")
			//       .test(
			//         "is-future-date",
			//         "Expiration date must be in the future",
			//         function (value) {
			//           // this refers to the expirationDate field value
			//           if (!value) {
			//             return false;
			//           }
			//           return isAfter(value!, new Date());
			//         }
			//       ),
			//   })
			// ),
		}),
	});
	const handleSubmit = () => {
		formikProps.validateForm().then((res) => {
			if (Object.values(res).length === 0) {
				UpdateCompany(formikProps.values);
			} else {
				addNewAlert({
					type: "error",
					message: "There are errors with the above fields",
				});
			}
		});
	};
	//console.log(formikProps.values.adjustments?.length)

	//function for increaing duration dynamically
	const increaseByFive = (n: number) => {
		let result = 0.15; // start with initial value of 0.15
		for (let i = 0; i < n; i++) {
			result += 0.03; // increase by 0.03 each time
		}
		return result;
	};

	useEffect(() => {
		if (!formikProps.values.isCanonical) return;

		const load = async () => {
			const data = await fetchVariations(Number(companyId));
			setVariations(data);
		};

		load();
	}, [formikProps.values.isCanonical, companyId]);

	useEffect(() => {
		if (!formikProps.values.canonicalCompanyId) return;

		const load = async () => {
			const data = await fetchCanonicalCompany(Number(companyId));
			setVariations(data);
		};

		load();
	}, [formikProps.values.canonicalCompanyId, companyId]);

	const { settings, loading } = useSelector(
		(state: RootState) => state.settings
	);
	const { labels } = useSelector((state: RootState) => state.labels);
	return (
		(formikProps && formikProps.values.id && (
			<CardContainer>
				<BrowserHeader
					title={`${labels.company.singular}: [${formikProps.values.id}] ${formikProps.values.name}`}
				/>
				<CardHeader>
					<h2 className="card-title">Update {labels.company.singular}</h2>
				</CardHeader>
				<CardBody>
					<FormikProvider value={formikProps}>
						<NewUserModal
							onClickHide={() => {
								setShowModal(false);
								getCompanyUsers(Number(companyId)).then((response) => {
									setCompanyUsers(response!);
								});
							}}
							show={showModal}
							companyId={formikProps.values.id}
						/>
						<NewCompanyModal
							settings={settings}
							loading={loading}
							onClickHide={() => {
								setShowCompanyModal(false);
								getCanonicalCompanies().then((response) => {
									setCanonicalCompanies(response || []);
								});
							}}
							show={showCompanyModal}
						/>
						<StickyContainer className="mb-[2rem] !relative !top-0 !min-h-min">
							<CollapsibleContainer
								className="mb-[2rem]"
								title={`${labels.company.singular} Information`}
							>
								<StickyCardBody className="!p-0">
									<CompanyInfo>
										<StyledForm
											className="twoColumn mb-[1rem] !gap-y-[2.5rem]"
											compact
										>
											<div className="col-w-100">
												<h3 className="subtitle !mb-0">Basic Info</h3>
											</div>
											<TextField
												name="name"
												placeholder={`${labels.company.singular} Name*`}
												value={formikProps.values.name}
											/>

											<DropdownField
												name="companyType"
												placeholder={`${labels.company.singular} Type*`}
												options={CompanyTypeOptions}
											/>
											<Authorized
												settings={settings}
												loading={loading}
												feature="Canonical Company"
												authorized={
													<>
														<div className="flex justify-between">
															<ToggleSwitchField
																name="isCanonical"
																disabled={
																	formikProps.values.canonicalCompanyId
																		? true
																		: false
																}
																label={`Is ${labels.canonical.singular} ${labels.company.singular}?`}
																checked={formikProps.values.isCanonical}
																id={"isCanonical"}
															/>
															<Button
																className="button-light w-[150px] float-right"
																type="button"
																onClick={() => {
																	setShowCompanyModal(true);
																}}
															>
																Add {labels.company.singular}
															</Button>
														</div>

														<DropdownField
															name="canonicalCompanyId"
															disabled={formikProps.values.isCanonical}
															placeholder={`${labels.canonical.singular} ${labels.company.singular}`}
															options={canonicalCompanies}
															onChange={async (e) => {
																const prevId =
																	formikProps.values.canonicalCompanyId; // old link
																const nextId =
																	e.target.value === ""
																		? null
																		: Number(e.target.value); // new link
																if (prevId === nextId) return; // nothing to do

																try {
																	await upsertCanonicalRelationship(
																		Number(companyId),
																		nextId
																	);

																	addNewAlert({
																		type: "success",
																		message: "Relationship updated.",
																	});
																} catch {
																	addNewAlert({
																		type: "error",
																		message: "Failed to update relationship.",
																	});
																}
															}}
														/>

														{/* Advanced fuzzy search */}
														{/* {!formikProps.values.isCanonical && (
                              <SearchBar
                                label={`Add ${labels.canonical.singular} ${labels.company.singular}`}
                                value={canonicalQuery}
                                onChange={(e) => {
                                  setCanonicalQuery(e.target.value);
                                  doSearch(e.target.value);
                                }}
                                onKeyUp={(e) => doSearch(e.currentTarget.value)}
                                dropdown={
                                  canonicalCompanies.length
                                    ? {
                                        name: `${labels.canonical.singular} ${labels.company.plural}`,
                                        options: canonicalCompanies,
                                        onChange: (value) => {
                                          const selectedValue = Number(
                                            value.target.value
                                          );
                                          const selectedOption =
                                            canonicalCompanies.find(
                                              (option) =>
                                                option.value === selectedValue
                                            );
                                          formikProps.setFieldValue(
                                            "canonicalCompanyId",
                                            selectedOption?.value
                                          );
                                        },
                                      }
                                    : undefined
                                }
                                onClickSearch={(val) => doSearch(val)}
                              />
                            )} */}
													</>
												}
											/>
											<Authorized
												settings={settings}
												loading={loading}
												feature="Company Agency Network"
												authorized={
													<DynamicDropdownField
														name="agencyNetworkId"
														category={assetsConfig.agencyNetworkCompanyName}
														value={formikProps.values.agencyNetworkId}
													/>
												}
											/>
											<Authorized
												settings={settings}
												loading={loading}
												feature="Company Holding Company"
												authorized={
													<DynamicDropdownField
														name="holdingCompanyId"
														category={assetsConfig.holdingCompanyName}
														value={formikProps.values.holdingCompanyId}
													/>
												}
											/>
											<Authorized
												settings={settings}
												loading={loading}
												feature="Company Address"
												authorized={
													<>
														<TextField
															name="address1"
															placeholder={`${labels.company.singular} Address 1*`}
															value={formikProps.values.address1}
														/>

														<TextField
															name="address2"
															placeholder={`${labels.company.singular} Address 2*`}
															value={formikProps.values.address2!}
														/>

														<TextField
															name="city"
															placeholder="City*"
															value={formikProps.values.city}
														/>
														<DropdownField
															name="country"
															placeholder="Country*"
															options={CountryOptions}
															onChange={(value) => {
																formikProps.setFieldValue("state", "");
															}}
														/>

														{formikProps.values.country === "United States" ||
														formikProps.values.country === "Canada" ? (
															<DropdownField
																name="state"
																placeholder="State/Province/Region*"
																options={
																	formikProps.values.country === "United States"
																		? USStateOptions
																		: CanadaStateOptions
																}
															/>
														) : (
															<TextField
																name="state"
																placeholder="State/Province/Region"
															/>
														)}

														<TextField
															name="postalCode"
															placeholder="Postal Code*"
															value={formikProps.values.postalCode}
														/>
													</>
												}
											/>
											<div className="col-w-100">
												<h3 className="subtitle !mb-0">Entry Contact</h3>
											</div>
											<TextField
												name="entryContactName"
												placeholder={`${labels.entry.singular} Contact Name`}
												value={formikProps.values.entryContactName}
											/>
											<TextField
												name="entryContactEmail"
												placeholder={`${labels.entry.singular} Contact Email`}
												value={formikProps.values.entryContactEmail}
											/>
											<DropdownField
												name="entryContactJobTitle"
												placeholder={`${labels.entry.singular} Contact Job Title* `}
												options={JobTitleOptions}
											/>
											<TextField
												name="entryContactPhone"
												placeholder={`${labels.entry.singular} Contact Phone`}
												value={formikProps.values.entryContactPhone}
											/>
											<div className="col-w-100">
												<h3 className="subtitle !mb-0">Finance Contact</h3>
											</div>
											<TextField
												name="financeContactName"
												placeholder="Finance Contact Name"
												value={formikProps.values.financeContactName}
											/>
											<TextField
												name="financeContactEmail"
												placeholder="Finance Contact Email"
												value={formikProps.values.financeContactEmail}
											/>
											<DropdownField
												name="financeContactJobTitle"
												placeholder="Finance Contact Job Title* "
												options={JobTitleOptions}
											/>
											<TextField
												name="financeContactPhone"
												placeholder="Finance Contact Phone"
												value={formikProps.values.financeContactPhone}
											/>

											<Authorized
												settings={settings}
												loading={loading}
												feature="Tax ID"
												authorized={
													<>
														<div className="col-w-100">
															<h3 className="subtitle !mb-0">Tax ID</h3>
														</div>
														<TextField
															name="taxID"
															placeholder="Company Tax ID"
															value={formikProps.values.taxID}
														/>
														<div></div>
													</>
												}
											/>
											<Authorized
												settings={settings}
												loading={loading}
												feature="Company Social Fields"
												authorized={
													<>
														<div className="col-w-100">
															<h3 className="subtitle !mb-0">
																Social Media Accounts
															</h3>
														</div>
														<TextField
															name="socialMedia1"
															placeholder="LinkedIn"
															value={formikProps.values.socialMedia1}
														/>
														<TextField
															name="socialMedia2"
															placeholder="X (Formerly Twitter)"
															value={formikProps.values.socialMedia2}
														/>
														<TextField
															name="socialMedia3"
															placeholder="Instagram"
															value={formikProps.values.socialMedia3}
														/>
														<TextField
															name="socialMedia4"
															placeholder="Facebook"
															value={formikProps.values.socialMedia4}
														/>
													</>
												}
											/>

											<div className="col-w-100 flex flex-col gap-[2rem] flex-1">
												<h3 className="subtitle !mb-0">Adjustments</h3>
												<AdjustmentFields companyId={formikProps.values.id!} />
											</div>
										</StyledForm>

										<div className="max-w-[650px] grid grid-cols-0 justify-end gap-[1rem] mt-[1rem] ml-auto">
											<Button
												// onClick={() => {
												//   console.log(formikProps.values);
												//   formikProps.submitForm();
												// }}
												onClick={() => handleSubmit()}
												disabled={formikProps.isSubmitting}
											>
												Update {labels.company.singular}
											</Button>
										</div>
									</CompanyInfo>
								</StickyCardBody>
							</CollapsibleContainer>

							<CollapsibleContainer
								className="mb-[2rem]"
								title={`${labels.company.singular} Metadata`}
							>
								<StickyCardBody className="!p-0">
									<EntryReviewContainer className="p-[2.25rem]">
										<EntryField
											label="Date Created"
											value={
												formikProps.values.createdDate
													? getLocalTime(formikProps.values.createdDate, true)
													: ""
											}
											isRequired={false}
										/>
										<EntryField
											label="Date Last Modified"
											value={
												formikProps.values.updatedDate
													? getLocalTime(formikProps.values.updatedDate, true)
													: ""
											}
											isRequired={false}
										/>
										<EntryField
											label="Last Modified User"
											value={formikProps.values.modifiedByName}
											hardLink={`/edit-user/${formikProps.values.modifiedBy}`}
											isRequired={false}
										/>
										<EntryField
											label={`${labels.company.singular} Id`}
											value={formikProps.values.id}
											isRequired={false}
										/>
										<EntryField
											label="Media Library"
											value={formikProps.values.id}
											isRequired={false}
											hardLink={`/admin/edit-entry/media-library/${formikProps.values.id}`}
											linkText={`/admin/edit-entry/media-library/${formikProps.values.id}`}
										/>
										<EntryField
											label="Individual Credits"
											value={formikProps.values.id}
											isRequired={false}
											hardLink={`/admin/edit-entry/credit-library/individual/${formikProps.values.id}`}
											linkText={`/admin/edit-entry/credit-library/individual/${formikProps.values.id}`}
										/>

										<EntryField
											label={`${labels.company.singular} Credits`}
											value={formikProps.values.id}
											isRequired={false}
											hardLink={`/admin/edit-entry/credit-library/company/${formikProps.values.id}`}
											linkText={`/admin/edit-entry/credit-library/company/${formikProps.values.id}`}
										/>

										<EntryField
											label={`${labels.company.singular} Tags`}
											value={formikProps.values.id}
											isRequired={false}
											hardLink={`/admin/edit-entry/tag-library/${formikProps.values.id}`}
											linkText={`/admin/edit-entry/tag-library/${formikProps.values.id}`}
										/>
									</EntryReviewContainer>
								</StickyCardBody>
							</CollapsibleContainer>

							<CollapsibleContainer
								className="mb-[2rem]"
								title={`${labels.company.singular} Users`}
							>
								<Button
									className="my-[1rem] ml-auto mr-[1rem]"
									type="button"
									onClick={() => {
										setShowModal(true);
									}}
								>
									Add User to {labels.company.singular}
								</Button>
								<StickyCardBody className="!p-0">
									<Table
										isActive={companyUsers.length > 0}
										dualScroll
										columnLabels={[
											"Profile",
											"User Id",
											"User Name",
											"Job Title",
											"Email",
											"Phone",
											"Enabled",
										]}
									>
										{companyUsers.map((companyUser: NemoUser, index) => {
											const entry = companyUser;

											return (
												<Fragment key={entry.id}>
													<div className="cell">
														<img
															src={
																(companyUser.profileUrl &&
																	companyUser.profileUrl!) ||
																Placeholder
															}
															alt={`${labels.entry.singular} Cover Image`}
														/>
													</div>
													<div className="cell">
														<p>
															<StyledLink
																href={`/edit-user/${companyUser.id}`}
																target="_blank"
															>
																{companyUser.id}
															</StyledLink>
														</p>
													</div>
													<div className="cell">
														<p>
															<StyledLink
																href={`/edit-user/${companyUser.id}`}
																target="_blank"
															>
																{companyUser.fullName}
															</StyledLink>
														</p>
													</div>
													<div className="cell">
														<p>{companyUser.jobTitle}</p>
													</div>
													<div className="cell">
														<p>{companyUser.email}</p>
													</div>
													<div className="cell">
														<p>{companyUser.phone}</p>
													</div>
													<div className="cell">
														<CheckboxField
															checked={companyUser.isEnabled}
															name="isEnabled"
															placeholder=""
															onChange={() => {
																adminEnable(
																	companyUser.id,
																	!companyUser.isEnabled
																);
																setCompanyUsers((prev) => {
																	return prev.map((user) =>
																		user.id === companyUser.id
																			? { ...user, isEnabled: !user.isEnabled }
																			: user
																	);
																});
															}}
														/>
													</div>
												</Fragment>
											);
										})}
									</Table>
								</StickyCardBody>
							</CollapsibleContainer>

							<CollapsibleContainer
								className="mb-[2rem]"
								title={`${labels.company.singular} ${labels.entry.plural}`}
							>
								{companyEntries.length > 0 && (
									<Button
										className="justify-self-end mt-4 ml-4 mr-4"
										onClick={exportAllCompanyEntriesToExcel}
										disabled={isExporting}
									>
										{isExporting ? (
											<LoadingSpinner src={config.assets.loading.primary} />
										) : (
											"Export Entries"
										)}
									</Button>
								)}
								<StickyCardBody className="!p-[1rem]">
									<EntryGrid
										companyId={companyId}
										onEntriesLoaded={setCompanyEntries}
									/>
								</StickyCardBody>
							</CollapsibleContainer>

							<Authorized
								settings={settings}
								loading={loading}
								feature="Canonical Company"
								authorized={
									<>
										<CollapsibleContainer
											className="mb-[2rem]"
											title={
												formikProps.values.isCanonical
													? `${labels.variation.singular} ${labels.company.plural}`
													: `${labels.canonical.singular} ${labels.company.singular}`
											}
										>
											{/* <Button
                        className="my-[1rem] ml-auto mr-[1rem]"
                        type="button"
                        onClick={() => {
                          setShowModal(true);
                        }}
                      >
                        Add {labels.variation.singular}{" "}
                        {labels.company.singular}
                      </Button> */}

											<StickyCardBody className="!px-0">
												<Table
													isActive={variations.length > 0}
													columnLabels={["Id", "Name", "City", "Country"]}
												>
													{variations.map((variant) => {
														return (
															<Fragment key={variant.id}>
																<div className="cell">
																	<p>
																		<StyledLink
																			href={`/edit-company/${variant.id}`}
																			target="_blank"
																		>
																			{variant.id}
																		</StyledLink>
																	</p>
																</div>

																<div className="cell">
																	<StyledLink
																		href={`/edit-company/${variant.id}`}
																		target="_blank"
																	>
																		{variant.name}
																	</StyledLink>
																</div>
																<div className="cell">
																	<p>{variant.city || "—"}</p>
																</div>
																<div className="cell">
																	<p>{variant.country || "—"}</p>
																</div>
															</Fragment>
														);
													})}
												</Table>
											</StickyCardBody>
										</CollapsibleContainer>
									</>
								}
							/>

							<CollapsibleContainer
								className="mb-[2rem]"
								title="Payment Method Settings"
							>
								<StickyCardBody>
									<div className="flex flex-col gap-[1rem]">
										<CheckboxField
											checked={formikProps.values.blockDelayedPayments}
											name="blockDelayedPayments"
											placeholder="Block Delayed Payments"
										/>
										<CheckboxField
											checked={formikProps.values.overridePaymentMethod}
											name="overridePaymentMethod"
											placeholder="Override Payment Method Restrictions"
										/>
									</div>

									<Button
										className="mt-[1rem] ml-auto"
										onClick={() => handleSubmit()}
										disabled={formikProps.isSubmitting}
									>
										Update {labels.company.singular}
									</Button>
								</StickyCardBody>
							</CollapsibleContainer>
						</StickyContainer>
					</FormikProvider>
				</CardBody>
			</CardContainer>
		)) || (
			<>
				<Loading />
			</>
		)
	);
};

export default EditCompany;
